import cloneDeep from "lodash/cloneDeep";
import isPlainObject from "lodash/isPlainObject";
import type {
  BaseBlock,
  BaseProp,
} from "@circle-react-shared/BuilderUI/DataModel.types";
import type { ChildrenProp, ExtendedTreeItem, TreeItem } from "../types";
import { TreeOperations } from "./TreeOperations";

const getPropertyNodes = (children: TreeItem[]): ExtendedTreeItem[] => {
  const property: ExtendedTreeItem[] = [];
  for (const child of children) {
    if (TreeOperations.isPropertyNode(child)) {
      property.push(child as ExtendedTreeItem);
    }
  }
  return property;
};

const getRegularChildren = (children: TreeItem[]): TreeItem[] => {
  const regular: TreeItem[] = [];
  for (const child of children) {
    if (!TreeOperations.isPropertyNode(child)) {
      regular.push(child);
    }
  }
  return regular;
};

const createUpdatedChildrenProp = (
  originalProp: ChildrenProp,
  children: TreeItem[],
  rebuildFromTree: (items: TreeItem[]) => BaseBlock[],
): ChildrenProp => ({
  ...originalProp,
  value: rebuildFromTree(children),
});

const rebuildPropsWithPropertyNodes = (
  props: Record<string, unknown>,
  propertyNodes: ExtendedTreeItem[],
  rebuildFromTree: (items: TreeItem[]) => BaseBlock[],
): Record<string, BaseProp> => {
  const sortedProps: Record<string, BaseProp> = {};
  const propertyNodeMap = new Map(propertyNodes.map(node => [node.type, node]));

  for (const [key, value] of Object.entries(props)) {
    if (TreeOperations.isChildrenProp(value)) {
      const propNode = propertyNodeMap.get(key);
      if (propNode) {
        sortedProps[key] = createUpdatedChildrenProp(
          value,
          propNode.children || [],
          rebuildFromTree,
        );
        propertyNodeMap.delete(key);
      } else {
        sortedProps[key] = value;
      }
    } else {
      sortedProps[key] = value as BaseProp;
    }
  }

  for (const [key, propNode] of propertyNodeMap) {
    const originalProp = props[key];
    if (originalProp && TreeOperations.isChildrenProp(originalProp)) {
      sortedProps[key] = createUpdatedChildrenProp(
        originalProp,
        propNode.children || [],
        rebuildFromTree,
      );
    }
  }

  return sortedProps;
};

const rebuildPropsWithRegularChildren = (
  props: Record<string, unknown>,
  regularChildren: TreeItem[],
  rebuildFromTree: (items: TreeItem[]) => BaseBlock[],
): Record<string, BaseProp> => {
  const newProps = { ...props } as Record<string, BaseProp>;

  for (const [key, prop] of Object.entries(newProps)) {
    if (TreeOperations.isChildrenProp(prop)) {
      newProps[key] = createUpdatedChildrenProp(
        prop,
        regularChildren,
        rebuildFromTree,
      );
      break;
    }
  }

  return newProps;
};

const processBlockAndChildren = (
  block: BaseBlock,
  map: Map<string, BaseBlock>,
  visited: Set<string>,
  stack: BaseBlock[],
): void => {
  visited.add(block.id);
  map.set(block.id, block);

  if (!block.props) return;

  const childBlocks = TreeOperations.extractChildBlocks(block.props);
  for (const child of childBlocks) {
    if (!visited.has(child.id)) {
      stack.push(child);
    }
  }
};

export const BlockOperations = {
  buildBlockMap: (originalBlocks: BaseBlock[]): Map<string, BaseBlock> => {
    const map = new Map<string, BaseBlock>();
    const visited = new Set<string>();
    const stack = [...originalBlocks];

    while (stack.length) {
      const block = stack.pop();
      if (!block) continue;

      if (!visited.has(block.id)) {
        processBlockAndChildren(block, map, visited, stack);
      }
    }
    return map;
  },

  updateBlockProps: (
    props: Record<string, unknown>,
    item: TreeItem,
    rebuildFromTree: (items: TreeItem[]) => BaseBlock[],
  ): Record<string, BaseProp> => {
    if (!item.children?.length) {
      return props as Record<string, BaseProp>;
    }

    const propertyNodes = getPropertyNodes(item.children);

    return propertyNodes.length > 0
      ? rebuildPropsWithPropertyNodes(props, propertyNodes, rebuildFromTree)
      : rebuildPropsWithRegularChildren(
          props,
          getRegularChildren(item.children),
          rebuildFromTree,
        );
  },

  processBlock: (
    item: TreeItem,
    blockMap: Map<string, BaseBlock>,
    usedBlockIds: Set<string>,
    rebuildFromTree: (items: TreeItem[]) => BaseBlock[],
  ): BaseBlock | null => {
    if (TreeOperations.isPropertyNode(item) || usedBlockIds.has(item.id))
      return null;

    const originalBlock = blockMap.get(item.id);
    if (!originalBlock) return null;

    usedBlockIds.add(item.id);
    const newBlock = cloneDeep(originalBlock);

    if (newBlock.props && isPlainObject(newBlock.props)) {
      newBlock.props = BlockOperations.updateBlockProps(
        newBlock.props,
        item,
        rebuildFromTree,
      );
    }

    return newBlock;
  },
} as const;
